import { FC, useCallback, useEffect, useMemo, useState } from "react";
import Drawer from "react-modern-drawer";
import { Button, ButtonSize, ButtonVariant } from "shared/components/Button";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components/IconButton";
import { Show } from "shared/components/helpers/Show";
import { Cancel01Icon } from "@hugeicons-pro/core-solid-rounded";
import { FilterSection } from "shared/app/LeadsTableView/components/FiltersDrawers/FilterSection";
import { PropertyKind, propertyKindLabel } from "shared/types";
import { toPropertyKinds } from "shared/utils/property-kind-label";
import { useVerificationTemplatesController } from "views/verification-templates/controller";
import { usePrescreeningTemplatesController } from "views/listing-settings/controller";
import { US_STATES } from "views/market-rent/constants";
import { getTemplateIdentifier } from "utils/template-identifier";
import { PropertiesFilters } from "../../types";

const PROPERTY_TYPE_OPTIONS = [
  {
    id: PropertyKind.MultiFamily,
    name: propertyKindLabel[PropertyKind.MultiFamily]
  },
  {
    id: PropertyKind.SingleFamily,
    name: propertyKindLabel[PropertyKind.SingleFamily]
  },
  {
    id: PropertyKind.ManufacturedHousing,
    name: propertyKindLabel[PropertyKind.ManufacturedHousing]
  }
];

const STATE_OPTIONS = US_STATES.map((s) => ({ id: s, name: s }));

interface Props {
  open: boolean;
  onClose: () => void;
  filters: PropertiesFilters;
  onApply: (filters: PropertiesFilters) => void;
}

export const PropertiesFiltersDrawer: FC<Props> = ({
  open,
  onClose,
  filters,
  onApply
}) => {
  const { templates: verificationTemplates } =
    useVerificationTemplatesController();
  const { templates: prescreeningTemplates } =
    usePrescreeningTemplatesController();

  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [selectedKinds, setSelectedKinds] = useState<string[]>([]);
  const [selectedScreeningTemplates, setSelectedScreeningTemplates] = useState<
    { id: string; name: string }[]
  >([]);
  const [selectedListingTemplates, setSelectedListingTemplates] = useState<
    { id: string; name: string }[]
  >([]);

  useEffect(() => {
    if (open) {
      setSelectedStates(filters.states ?? []);
      setSelectedKinds((filters.propertyTypes as string[]) ?? []);
      setSelectedScreeningTemplates(filters.screeningTemplates ?? []);
      setSelectedListingTemplates(filters.listingTemplates ?? []);
    }
  }, [open, filters]);

  const toggleStringItem = useCallback(
    (value: string, setter: React.Dispatch<React.SetStateAction<string[]>>) => {
      setter((prev) =>
        prev.includes(value)
          ? prev.filter((v) => v !== value)
          : [...prev, value]
      );
    },
    []
  );

  const toggleObjectItem = useCallback(
    (
      id: string,
      name: string,
      setter: React.Dispatch<
        React.SetStateAction<{ id: string; name: string }[]>
      >
    ) => {
      setter((prev) =>
        prev.some((item) => item.id === id)
          ? prev.filter((item) => item.id !== id)
          : [...prev, { id, name }]
      );
    },
    []
  );

  const hasSelectedFilters =
    selectedStates.length > 0 ||
    selectedKinds.length > 0 ||
    selectedScreeningTemplates.length > 0 ||
    selectedListingTemplates.length > 0;

  const handleApply = () => {
    onApply({
      states: selectedStates.length ? selectedStates : undefined,
      propertyTypes: selectedKinds.length
        ? toPropertyKinds(selectedKinds)
        : undefined,
      screeningTemplates: selectedScreeningTemplates.length
        ? selectedScreeningTemplates
        : undefined,
      listingTemplates: selectedListingTemplates.length
        ? selectedListingTemplates
        : undefined
    });
    onClose();
  };

  const handleUnselectAll = () => {
    setSelectedStates([]);
    setSelectedKinds([]);
    setSelectedScreeningTemplates([]);
    setSelectedListingTemplates([]);
  };

  const screeningOptions = useMemo(
    () =>
      verificationTemplates?.map((t) => ({
        id: getTemplateIdentifier(t),
        name: t.name
      })) ?? [],
    [verificationTemplates]
  );

  const listingOptions = useMemo(
    () =>
      prescreeningTemplates?.map((t) => ({
        id: t.identifier,
        name: t.name
      })) ?? [],
    [prescreeningTemplates]
  );

  return (
    <Drawer
      className="rounded-l-lg"
      direction="right"
      open={open}
      onClose={onClose}
      size={360}
    >
      <div className="flex flex-col h-full px-6">
        <div className="flex items-center justify-between py-4 border-b border-alpha-40">
          <h1 className="text-xl font-bold text-content-1000">Filters</h1>
          <IconButton
            variant={IconButtonVariant.TEXT_BASE}
            size={IconButtonSize.SM}
            onClick={onClose}
            HugeIcon={Cancel01Icon}
          />
        </div>

        <Show if={hasSelectedFilters}>
          <div className="py-4 border-b border-alpha-40">
            <Button
              variant={ButtonVariant.TEXT}
              className="ml-2 text-base font-bold"
              onClick={handleUnselectAll}
            >
              Unselect all
            </Button>
          </div>
        </Show>

        <div className="flex-1 overflow-y-auto divide-y divide-alpha-40">
          <FilterSection
            title="State"
            options={STATE_OPTIONS}
            optionLimit={8}
            selectedOptions={selectedStates}
            onChange={(value) => toggleStringItem(value, setSelectedStates)}
          />

          <FilterSection
            title="Property Type"
            showSearch={false}
            options={PROPERTY_TYPE_OPTIONS}
            selectedOptions={selectedKinds}
            onChange={(value) => toggleStringItem(value, setSelectedKinds)}
          />

          <Show if={screeningOptions.length > 0}>
            <FilterSection
              title="Screening Template"
              options={screeningOptions}
              optionLimit={5}
              selectedOptions={selectedScreeningTemplates.map((t) => t.id)}
              onChange={(id) => {
                const found = screeningOptions.find((o) => o.id === id);
                toggleObjectItem(
                  id,
                  found?.name ?? "",
                  setSelectedScreeningTemplates
                );
              }}
            />
          </Show>

          <Show if={listingOptions.length > 0}>
            <FilterSection
              title="Listing Template"
              options={listingOptions}
              optionLimit={5}
              selectedOptions={selectedListingTemplates.map((t) => t.id)}
              onChange={(id) => {
                const found = listingOptions.find((o) => o.id === id);
                toggleObjectItem(
                  id,
                  found?.name ?? "",
                  setSelectedListingTemplates
                );
              }}
            />
          </Show>
        </div>

        <div className="py-4 flex gap-2 border-t border-alpha-40">
          <Button
            className="w-[152px]"
            size={ButtonSize.BASE}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            className="w-[152px]"
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.BASE}
            onClick={handleApply}
          >
            Apply
          </Button>
        </div>
      </div>
    </Drawer>
  );
};
